import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { getCourse, updateCourse, deleteCourse, getInstructors, getStudents, enrollStudent, unenrollStudent, createModule, createAssignment, updateAssignment } from '../../../api/adminApi';
import Badge from '../../../components/admin/Badge';
import Toast from '../../../components/admin/Toast';
import ConfirmModal from '../../../components/admin/ConfirmModal';
import PageLoader from '../../../components/common/PageLoader';
import { toTitleCase } from '../../../utils/format';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';

const AV_COLORS = ['#5B6ABF','#1A7048','#E8440A','#D4882A','#8A6FB0','#2E8B8B','#C74D3A','#F7C91B'];
function avColor(id) {
  let h = 0;
  for (let i = 0; i < String(id).length; i++) h = String(id).charCodeAt(i) + ((h << 5) - h);
  return AV_COLORS[Math.abs(h) % AV_COLORS.length];
}
function initials(name = '') { return name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase(); }

const TABS = [
  { key: 'overview',    icon: '◎',  label: 'Overview'    },
  { key: 'modules',     icon: '⊞',  label: 'Modules'     },
  { key: 'assignments', icon: '📋', label: 'Assignments' },
  { key: 'students',    icon: '◈',  label: 'Students'    },
];

export default function CourseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') || 'overview';
  function setTab(t) { setSearchParams({ tab: t }); }

  const [data,        setData]        = useState(null);
  const [showDelete,  setShowDelete]  = useState(false);
  const [loading,     setLoading]     = useState(true);
  const [form,        setForm]        = useState({});
  const [saving,      setSaving]      = useState(false);
  const [toast,       setToast]       = useState(null);
  const [enrollOpen,   setEnrollOpen]   = useState(false);
  const [allStudents,  setAllStudents]  = useState([]);
  const [selectedIds,  setSelectedIds]  = useState(new Set());
  const [enrolling,    setEnrolling]    = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(null); // { id, name }
  const [removing,      setRemoving]      = useState(false);
  const [moduleOpen,    setModuleOpen]    = useState(false);
  const [moduleForm,    setModuleForm]    = useState({ title: '', overview: '' });
  const [addingModule,  setAddingModule]  = useState(false);
  const [instructors,   setInstructors]   = useState([]);
  const [assignModal,   setAssignModal]   = useState(false);
  const [selAssign,     setSelAssign]     = useState(null);
  const [assignForm,    setAssignForm]    = useState({ title: '', description: '', link: '', due_date: '', max_score: 100, accepted_file_types: 'PDF,DOCX' });
  const [savingAssign,  setSavingAssign]  = useState(false);

  const load = () => {
    setLoading(true);
    getCourse(id).then(d => {
      setData(d);
      setForm({
        title:         d.course.title,
        code:          d.course.code || '',
        description:   d.course.description || '',
        emoji:         d.course.emoji,
        accent_color:  d.course.accent_color,
        status:        d.course.status,
        capacity:      d.course.capacity || 30,
        start_date:    d.course.start_date || '',
        end_date:      d.course.end_date || '',
        instructor_id: d.course.instructor?.id ? String(d.course.instructor.id) : '',
      });
    }).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    getInstructors().then(d => setInstructors(d.instructors)).catch(console.error);
  }, [id]);

  async function handleSave() {
    if (form.start_date && form.end_date && form.start_date >= form.end_date) {
      setToast({ msg: 'Start date must be before end date.', type: 'error' });
      return;
    }
    if (form.instructor_id) {
      const inst = instructors.find(i => String(i.id) === String(form.instructor_id));
      if (inst && inst.status !== 'Active') {
        setToast({ msg: 'Cannot assign an inactive instructor. Please select an active instructor.', type: 'error' });
        return;
      }
    }
    setSaving(true);
    try {
      await updateCourse(id, {
        ...form,
        instructor_id: form.instructor_id ? Number(form.instructor_id) : null,
      });
      setToast('Course updated');
      load();
    } catch (err) {
      setToast({ msg: err.message, type: 'error' });
    } finally {
      setSaving(false);
    }
  }

  async function handleUnenroll() {
    if (!confirmRemove) return;
    setRemoving(true);
    try {
      await unenrollStudent(confirmRemove.id, Number(id));
      setConfirmRemove(null);
      load();
      setToast(`${confirmRemove.name} removed from course`);
    } catch (err) {
      setToast({ msg: err.message, type: 'error' });
    } finally {
      setRemoving(false);
    }
  }

  async function handleAddModule(e) {
    e.preventDefault();
    setAddingModule(true);
    try {
      await createModule(id, moduleForm);
      setModuleOpen(false);
      setModuleForm({ title: '', overview: '' });
      setToast('Module added');
      load();
    } catch (err) {
      setToast({ msg: err.message, type: 'error' });
    } finally {
      setAddingModule(false);
    }
  }

  function openAddAssignment() {
    setSelAssign(null);
    setAssignForm({ title: '', description: '', link: '', due_date: '', max_score: 100, accepted_file_types: 'PDF,DOCX' });
    setAssignModal(true);
  }

  function openEditAssignment(a) {
    setSelAssign(a);
    setAssignForm({
      title: a.title,
      description: a.description || '',
      link: a.link || '',
      due_date: a.due_date ? a.due_date.slice(0, 10) : '',
      max_score: a.max_score,
      accepted_file_types: (a.accepted_file_types || []).join(', '),
    });
    setAssignModal(true);
  }

  async function handleSaveAssignment(e) {
    e.preventDefault();
    setSavingAssign(true);
    try {
      const body = {
        ...assignForm,
        accepted_file_types: assignForm.accepted_file_types.split(',').map(s => s.trim()).filter(Boolean),
        max_score: Number(assignForm.max_score),
      };
      if (selAssign) {
        await updateAssignment(id, selAssign.id, body);
      } else {
        await createAssignment(id, body);
      }
      setAssignModal(false);
      setSelAssign(null);
      load();
      setToast(selAssign ? 'Assignment updated' : 'Assignment created');
    } catch (err) {
      setToast({ msg: err.message, type: 'error' });
    } finally {
      setSavingAssign(false);
    }
  }

  async function handlePublishAssignment(aId, currentStatus) {
    const newStatus = currentStatus === 'published' ? 'draft' : 'published';
    try {
      await updateAssignment(id, aId, { status: newStatus });
      load();
      setToast(`Assignment ${newStatus === 'published' ? 'published' : 'set to draft'}`);
    } catch (err) {
      setToast({ msg: err.message, type: 'error' });
    }
  }

  function openEnroll() {
    setSelectedIds(new Set());
    getStudents().then(d => setAllStudents(d.students)).catch(console.error);
    setEnrollOpen(true);
  }

  function toggleStudent(sid) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(sid) ? next.delete(sid) : next.add(sid);
      return next;
    });
  }

  async function handleEnroll() {
    if (selectedIds.size === 0) return;
    const capacity = course.capacity ?? 30;
    const remaining = capacity - enrolled_students.length;
    if (selectedIds.size > remaining) {
      setToast({ msg: `Cannot enroll ${selectedIds.size} student${selectedIds.size !== 1 ? 's' : ''} — only ${remaining} spot${remaining !== 1 ? 's' : ''} remaining (capacity: ${capacity}).`, type: 'error' });
      return;
    }
    setEnrolling(true);
    let enrolled = 0, failed = 0;
    for (const sid of selectedIds) {
      try {
        await enrollStudent({ student_id: Number(sid), course_id: Number(id) });
        enrolled++;
      } catch {
        failed++;
      }
    }
    setEnrolling(false);
    setEnrollOpen(false);
    load();
    if (failed === 0) {
      setToast(`${enrolled} student${enrolled !== 1 ? 's' : ''} enrolled successfully`);
    } else {
      setToast({ msg: `${enrolled} enrolled, ${failed} failed (already enrolled?)`, type: 'error' });
    }
  }

  if (loading || !data) return <div className="body"><PageLoader /></div>;

  const { course, modules, assignments, enrolled_students } = data;
  const pubModules = modules.filter(m => m.status === 'published').length;

  return (
    <>
      {/* ── Page Header ── */}
      <div className="ph">
        <div>
          <div className="bc">Admin / Courses / {toTitleCase(course.title)}</div>
          <div className="pt" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 22 }}>{course.emoji}</span>
            {toTitleCase(course.title)}
          </div>
          <div className="ps">
            {toTitleCase(course.instructor?.full_name || 'No instructor')}
            {' · '}{enrolled_students.length} student{enrolled_students.length !== 1 ? 's' : ''}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-o btn-sm" onClick={() => navigate('/admin/courses')}>← All Courses</button>
          <button className="btn btn-d btn-sm" onClick={() => setShowDelete(true)}>🗑 Delete Course</button>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="body">

        {/* ── Horizontal Tab Bar ── */}
        <div className="tab-bar">
          {TABS.map(t => (
            <button
              key={t.key}
              className={`tab-item${tab === t.key ? ' on' : ''}`}
              onClick={() => setTab(t.key)}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {/* ══ Overview ══ */}
        {tab === 'overview' && (
          <>
            {/* Stat tiles */}
            <div className="stats">
              <div className="sc">
                <div className="sc-l">Modules</div>
                <div className="sc-v">{pubModules}/{modules.length}</div>
                <div className="sc-s" style={{ color: 'var(--muted)' }}>published</div>
              </div>
              <div className="sc">
                <div className="sc-l">Students</div>
                <div className="sc-v">{enrolled_students.length}</div>
                <div className="sc-s" style={{ color: 'var(--muted)' }}>enrolled</div>
              </div>
              <div className="sc">
                <div className="sc-l">Assignments</div>
                <div className="sc-v">{assignments.length}</div>
                <div className="sc-s" style={{ color: 'var(--muted)' }}>total</div>
              </div>
              <div className="sc">
                <div className="sc-l">Instructor</div>
                <div className="sc-v" style={{ fontSize: 14, lineHeight: 1.3, paddingTop: 4 }}>
                  {toTitleCase(
                    instructors.find(i => String(i.id) === String(form.instructor_id))?.full_name
                    || course.instructor?.full_name
                  ) || <span style={{ color: 'var(--subtle)', fontStyle: 'italic', fontSize: 12, fontFamily: 'Nunito Sans,sans-serif', fontWeight: 400 }}>Unassigned</span>
                  }
                </div>
              </div>
              <div className="sc">
                <div className="sc-l">Start Date</div>
                <div className="sc-v" style={{ fontSize: 14, lineHeight: 1.3, paddingTop: 4 }}>
                  {course.start_date
                    ? new Date(course.start_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                    : <span style={{ color: 'var(--subtle)', fontStyle: 'italic', fontSize: 12, fontFamily: 'Nunito Sans,sans-serif', fontWeight: 400 }}>Not set</span>
                  }
                </div>
              </div>
              <div className="sc">
                <div className="sc-l">End Date</div>
                <div className="sc-v" style={{ fontSize: 14, lineHeight: 1.3, paddingTop: 4 }}>
                  {course.end_date
                    ? new Date(course.end_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                    : <span style={{ color: 'var(--subtle)', fontStyle: 'italic', fontSize: 12, fontFamily: 'Nunito Sans,sans-serif', fontWeight: 400 }}>Not set</span>
                  }
                </div>
              </div>
            </div>

            {/* Always-editable Course Details card */}
            <div className="card">
              <div className="ct">
                Course Details
                <button className="btn btn-p btn-sm" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving…' : 'Save Changes'}
                </button>
              </div>

              <div className="fg">
                <label className="fl">Title</label>
                <input className="li" value={form.title || ''} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
              </div>

              <div className="fg">
                <label className="fl">Description</label>
                <CKEditor
                  editor={ClassicEditor}
                  data={form.description || ''}
                  onChange={(_, editor) => setForm(f => ({ ...f, description: editor.getData() }))}
                  config={{ toolbar: ['bold','italic','|','bulletedList','numberedList','|','blockQuote','link'] }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="fg">
                  <label className="fl">Assigned Instructor</label>
                  <select
                    className="li"
                    value={form.instructor_id || ''}
                    onChange={e => setForm(f => ({ ...f, instructor_id: e.target.value }))}
                  >
                    <option value="">— Unassigned —</option>
                    {instructors.filter(i => i.status === 'Active').map(i => (
                      <option key={i.id} value={String(i.id)}>{toTitleCase(i.full_name)}</option>
                    ))}
                    {instructors.some(i => i.status !== 'Active') && (
                      <optgroup label="── Inactive (cannot assign) ──">
                        {instructors.filter(i => i.status !== 'Active').map(i => (
                          <option key={i.id} value={String(i.id)} disabled>{toTitleCase(i.full_name)} (Inactive)</option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                </div>
                <div className="fg">
                  <label className="fl">Status</label>
                  <select className="li" value={form.status || 'Draft'} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                    <option>Draft</option>
                    <option>Published</option>
                    <option>Archived</option>
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="fg">
                  <label className="fl">Code</label>
                  <input className="li" placeholder="MKT-301" value={form.code || ''} onChange={e => setForm(f => ({ ...f, code: e.target.value }))} required />
                </div>
                <div className="fg">
                  <label className="fl">Capacity</label>
                  <input className="li" type="number" min={1} value={form.capacity || 30} onChange={e => setForm(f => ({ ...f, capacity: e.target.value }))} />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="fg">
                  <label className="fl">Start Date</label>
                  <input className="li" type="date" value={form.start_date || ''} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} />
                </div>
                <div className="fg" style={{ marginBottom: 0 }}>
                  <label className="fl">End Date</label>
                  <input className="li" type="date" value={form.end_date || ''} min={form.start_date || undefined} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} />
                </div>
              </div>
            </div>
          </>
        )}

        {/* ══ Modules ══ */}
        {tab === 'modules' && (
          <div className="card">
            <div className="ct">
              <span>Modules <span className="b b-y">{modules.length}</span></span>
              <button className="btn btn-o btn-sm" onClick={() => setModuleOpen(true)}>+ Add Module</button>
            </div>
            {modules.length === 0 ? (
              <div className="empty" style={{ padding: '36px 0' }}>
                <div className="empty-icon">⊞</div>
                <div>No modules yet</div>
              </div>
            ) : (
              modules.map((m, i) => {
                const checks = [
                  m.video_uploaded         ? '🎬' : '',
                  m.presentation_uploaded  ? '📑' : '',
                  (m.question_count  > 0)  ? '📝' : '',
                  (m.resource_count  > 0)  ? '📎' : '',
                ].filter(Boolean).join(' ');
                return (
                  <div key={m.id} className="mi">
                    <div className={`mn${m.status === 'published' ? ' done' : ''}`}>
                      {m.status === 'published' ? '✓' : (m.sort_order ?? i + 1)}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="mt">{m.title}</div>
                      <div className="ms">
                        {checks || 'No content yet'}
                        {m.question_count > 0 ? ` · ${m.question_count} quiz questions` : ''}
                        {m.resource_count > 0 ? ` · ${m.resource_count} resources` : ''}
                      </div>
                    </div>
                    <span className={`b ${m.status === 'published' ? 'b-g' : 'b-o'}`}>{m.status}</span>
                    <button
                      className="btn btn-o btn-sm"
                      onClick={() => navigate(`/admin/courses/${id}/modules/${m.id}`)}
                    >
                      Edit →
                    </button>
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* ══ Assignments ══ */}
        {tab === 'assignments' && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <div style={{ fontFamily: "'Nunito Sans',sans-serif", fontWeight: 700, fontSize: 14 }}>
                Assignments <span className="b b-y">{assignments.length} total</span>
              </div>
              <button className="btn btn-p btn-sm" onClick={openAddAssignment}>+ New Assignment</button>
            </div>

            {assignments.length === 0 ? (
              <div className="card" style={{ textAlign: 'center', padding: 44 }}>
                <div style={{ fontSize: 44, marginBottom: 14 }}>📋</div>
                <div style={{ fontFamily: "'Nunito Sans',sans-serif", fontWeight: 700, fontSize: 15, marginBottom: 6 }}>No assignments yet</div>
                <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 18, maxWidth: 340, margin: '0 auto 18px' }}>
                  Create assignments for students to submit their work.
                </p>
                <button className="btn btn-p btn-sm" onClick={openAddAssignment}>+ Create First Assignment</button>
              </div>
            ) : (
              assignments.map(a => (
                <div key={a.id} className="card" style={{ marginBottom: 14 }}>
                  <div className="ct">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 16 }}>📋</span>
                      <span>{a.title}</span>
                    </div>
                    <span className={`b ${a.status === 'published' ? 'b-g' : 'b-o'}`}>
                      {a.status === 'published' ? 'Published' : 'Draft'}
                    </span>
                  </div>

                  {a.description && (
                    <div className="rich-text" style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.65, marginBottom: 14 }}
                      dangerouslySetInnerHTML={{ __html: a.description }} />
                  )}
                  {a.link && (
                    <div style={{ marginBottom: 14 }}>
                      <a href={a.link} target="_blank" rel="noreferrer"
                        style={{ fontSize: 13, color: '#1A56DB', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 5, textDecoration: 'underline', textUnderlineOffset: 3 }}>
                        🔗 Reference Link ↗
                      </a>
                    </div>
                  )}

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 14 }}>
                    {[
                      { label: 'Due Date',   value: a.due_date ? new Date(a.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—' },
                      { label: 'Max Score',  value: `${a.max_score} pts` },
                      { label: 'File Types', value: (a.accepted_file_types || []).join(', ') || '—' },
                    ].map(({ label, value }) => (
                      <div key={label} style={{ padding: '10px 12px', background: 'var(--surface)', borderRadius: 'var(--rs)', border: '1px solid var(--border)' }}>
                        <div style={{ fontSize: 10, color: 'var(--muted)', fontFamily: "'DM Mono',monospace", textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 3 }}>{label}</div>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>{value}</div>
                      </div>
                    ))}
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
                    <button className="btn btn-o btn-sm" onClick={() => openEditAssignment(a)}>Edit</button>
                    <button
                      className={`btn btn-sm ${a.status === 'published' ? 'btn-o' : 'btn-p'}`}
                      onClick={() => handlePublishAssignment(a.id, a.status)}
                    >
                      {a.status === 'published' ? 'Unpublish' : 'Publish'}
                    </button>
                  </div>
                </div>
              ))
            )}
          </>
        )}

        {/* ══ Students ══ */}
        {tab === 'students' && (
          <div className="card" style={{ padding: 0 }}>
            <div className="ct" style={{ padding: '16px 20px 0', marginBottom: 0 }}>
              Enrolled Students
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 12, color: 'var(--muted)' }}>
                  {enrolled_students.length}/{course.capacity ?? 30} enrolled
                </span>
                {course.status !== 'Published' ? (
                  <span
                    className="btn btn-o btn-sm"
                    style={{ opacity: 0.45, cursor: 'not-allowed' }}
                    title={`Enrollment is disabled — course is ${course.status}`}
                  >
                    + Enroll Student
                  </span>
                ) : (
                  <button className="btn btn-o btn-sm" onClick={openEnroll}>+ Enroll Student</button>
                )}
              </div>
            </div>
            {enrolled_students.length === 0 ? (
              <div className="empty"><div className="empty-icon">◈</div><div>No students enrolled yet</div></div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Email</th>
                    <th>Progress</th>
                    <th>Modules</th>
                    <th>Certificate</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {enrolled_students.map(s => (
                    <tr key={s.id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                          <div className="av" style={{ width: 28, height: 28, background: avColor(s.id), color: 'white', fontSize: 9, flexShrink: 0 }}>
                            {initials(s.full_name)}
                          </div>
                          <span style={{ fontWeight: 600 }}>{toTitleCase(s.full_name)}</span>
                        </div>
                      </td>
                      <td style={{ color: 'var(--muted)', fontSize: 12 }}>{s.email}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div className="pbar" style={{ width: 60 }}>
                            <div className="pfill" style={{ width: `${s.progress_percent}%` }} />
                          </div>
                          <span style={{ fontSize: 11, color: 'var(--muted)' }}>{s.progress_percent}%</span>
                        </div>
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--muted)' }}>{s.modules_completed}/{s.modules_total}</td>
                      <td>
                        {s.certificate_code
                          ? <span className="b b-g">✦ Issued</span>
                          : <span className="b b-gr">Pending</span>
                        }
                      </td>
                      <td>
                        <button
                          className="btn btn-o btn-sm"
                          style={{ fontSize: 11, color: 'var(--red, #C74D3A)', borderColor: 'var(--red, #C74D3A)' }}
                          onClick={() => setConfirmRemove({ id: s.id, name: s.full_name })}
                        >
                          Unenroll
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

      </div>{/* end body */}

      {/* ── Enroll Student Modal ── */}
      {enrollOpen && (
        <div className="modal-overlay">
          <div className="modal-box" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
            <div className="modal-title">
              Enroll Students — <span style={{ fontWeight: 400, fontSize: 13 }}>{toTitleCase(course.title)}</span>
              <button className="btn btn-o btn-sm" onClick={() => setEnrollOpen(false)}>✕</button>
            </div>
            {(() => {
              const capacity  = course.capacity ?? 30;
              const remaining = capacity - enrolled_students.length;
              // Only active, not-yet-enrolled students
              const available = allStudents.filter(s =>
                s.status === 'Active' && !enrolled_students.find(e => e.id === s.id)
              );
              const inactiveSkipped = allStudents.filter(s =>
                s.status !== 'Active' && !enrolled_students.find(e => e.id === s.id)
              ).length;

              return (
                <>
                  {/* Capacity bar */}
                  <div style={{ background: 'var(--surface)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 12.5 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, color: 'var(--ink)' }}>
                      <span>Capacity</span>
                      <span style={{ fontWeight: 600, color: remaining === 0 ? '#C74D3A' : 'var(--green)' }}>
                        {enrolled_students.length}/{capacity} enrolled · <strong>{remaining}</strong> spot{remaining !== 1 ? 's' : ''} left
                      </span>
                    </div>
                    <div style={{ height: 5, background: 'var(--border)', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${Math.min((enrolled_students.length / capacity) * 100, 100)}%`, background: remaining === 0 ? '#C74D3A' : 'var(--green)', borderRadius: 3 }} />
                    </div>
                  </div>

                  {remaining === 0 ? (
                    <div style={{ fontSize: 13, color: '#C74D3A', padding: '8px 0' }}>
                      ⚠️ This course has reached its capacity of {capacity} students.
                    </div>
                  ) : available.length === 0 ? (
                    <div style={{ fontSize: 12, color: 'var(--muted)', padding: '10px 0' }}>
                      No eligible students to enroll.{inactiveSkipped > 0 && ` (${inactiveSkipped} inactive student${inactiveSkipped !== 1 ? 's' : ''} hidden)`}
                    </div>
                  ) : (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                        <span style={{ fontSize: 12, color: 'var(--muted)' }}>
                          {selectedIds.size > 0
                            ? `${selectedIds.size} selected · ${remaining - selectedIds.size} spot${remaining - selectedIds.size !== 1 ? 's' : ''} remaining`
                            : `${available.length} active student${available.length !== 1 ? 's' : ''} available`}
                          {inactiveSkipped > 0 && ` · ${inactiveSkipped} inactive hidden`}
                        </span>
                        <button
                          className="btn btn-o btn-sm"
                          style={{ fontSize: 11 }}
                          onClick={() => {
                            const allSelected = available.every(s => selectedIds.has(String(s.id)));
                            if (allSelected) {
                              setSelectedIds(new Set());
                            } else {
                              // Select up to remaining capacity
                              const toSelect = available.slice(0, remaining).map(s => String(s.id));
                              setSelectedIds(new Set(toSelect));
                            }
                          }}
                        >
                          {available.every(s => selectedIds.has(String(s.id))) ? 'Deselect All' : 'Select All'}
                        </button>
                      </div>
                      <div className="course-pick-list" style={{ maxHeight: 300, overflowY: 'auto' }}>
                        {available.map(s => {
                          const sel      = selectedIds.has(String(s.id));
                          const atLimit  = !sel && selectedIds.size >= remaining;
                          return (
                            <div
                              key={s.id}
                              className={`course-pick-item${sel ? ' selected' : ''}`}
                              onClick={() => { if (!atLimit) toggleStudent(String(s.id)); }}
                              style={{ opacity: atLimit ? 0.45 : 1, cursor: atLimit ? 'not-allowed' : 'pointer' }}
                              title={atLimit ? `Only ${remaining} spot${remaining !== 1 ? 's' : ''} remaining` : ''}
                            >
                              <div className="course-pick-check">{sel ? '✓' : ''}</div>
                              <div className="av" style={{ width: 28, height: 28, background: avColor(s.id), color: 'white', fontSize: 9, flexShrink: 0 }}>
                                {initials(s.full_name)}
                              </div>
                              <div>
                                <div style={{ fontWeight: 600, fontSize: 13.5 }}>{toTitleCase(s.full_name)}</div>
                                <div style={{ fontSize: 11, color: 'var(--muted)' }}>{s.email}</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </>
              );
            })()}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
              <button type="button" className="btn btn-o" onClick={() => setEnrollOpen(false)}>Cancel</button>
              <button className="btn btn-p" onClick={handleEnroll} disabled={enrolling || selectedIds.size === 0}>
                {enrolling ? 'Enrolling…' : selectedIds.size > 0 ? `Enroll ${selectedIds.size} Student${selectedIds.size !== 1 ? 's' : ''}` : 'Enroll Students'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Add Module Modal ── */}
      {moduleOpen && (
        <div className="modal-overlay">
          <div className="modal-box" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div className="modal-title">
              Add Module
              <button className="btn btn-o btn-sm" onClick={() => setModuleOpen(false)}>✕</button>
            </div>
            <form onSubmit={handleAddModule}>
              <div className="fg">
                <label className="fl">Module Title</label>
                <input
                  className="li"
                  placeholder="e.g. Introduction to AI in Marketing"
                  value={moduleForm.title}
                  onChange={e => setModuleForm(f => ({ ...f, title: e.target.value }))}
                  required
                  autoFocus
                />
              </div>
              <div className="fg">
                <label className="fl">Overview <span style={{ color: 'var(--subtle)', fontWeight: 400 }}>(optional)</span></label>
                <CKEditor
                  editor={ClassicEditor}
                  data={moduleForm.overview || ''}
                  onChange={(_, editor) => setModuleForm(f => ({ ...f, overview: editor.getData() }))}
                  config={{ toolbar: ['bold','italic','|','bulletedList','numberedList','|','blockQuote','link'] }}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
                <button type="button" className="btn btn-o" onClick={() => setModuleOpen(false)}>Cancel</button>
                <button type="submit" className="btn btn-p" disabled={addingModule}>
                  {addingModule ? 'Adding…' : 'Add Module'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Confirm Remove Modal ── */}
      {confirmRemove && (
        <div className="modal-overlay">
          <div className="modal-box" style={{ maxWidth: 400 }} onClick={e => e.stopPropagation()}>
            <div className="modal-title">
              Unenroll Student
              <button className="btn btn-o btn-sm" onClick={() => setConfirmRemove(null)}>✕</button>
            </div>
            <p style={{ fontSize: 13.5, color: 'var(--ink)', lineHeight: 1.6, marginBottom: 4 }}>
              Are you sure you want to unenroll <strong>{confirmRemove.name}</strong> from <strong>{course.title}</strong>?
            </p>
            <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 0 }}>
              Their progress and submissions will be preserved but they will lose access to the course.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
              <button className="btn btn-o" onClick={() => setConfirmRemove(null)}>Cancel</button>
              <button
                className="btn btn-p"
                style={{ background: 'var(--red, #C74D3A)', borderColor: 'var(--red, #C74D3A)' }}
                disabled={removing}
                onClick={handleUnenroll}
              >
                {removing ? 'Unenrolling…' : 'Yes, Unenroll'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Add / Edit Assignment Modal ── */}
      {assignModal && (
        <div className="modal-overlay">
          <div className="modal-box" style={{ maxWidth: 500 }} onClick={e => e.stopPropagation()}>
            <div className="modal-title">
              {selAssign ? 'Edit Assignment' : 'New Assignment'}
              <button className="btn btn-o btn-sm" onClick={() => { setAssignModal(false); setSelAssign(null); }}>✕</button>
            </div>
            <form onSubmit={handleSaveAssignment}>
              <div className="fg">
                <label className="fl">Title</label>
                <input className="li" required placeholder="Assignment title"
                  value={assignForm.title}
                  onChange={e => setAssignForm(f => ({ ...f, title: e.target.value }))} />
              </div>
              <div className="fg">
                <label className="fl">Description</label>
                <CKEditor
                  key={selAssign?.id ?? 'new'}
                  editor={ClassicEditor}
                  data={assignForm.description || ''}
                  onChange={(_, editor) => setAssignForm(f => ({ ...f, description: editor.getData() }))}
                  config={{ toolbar: ['bold','italic','|','bulletedList','numberedList','|','blockQuote','link'] }}
                />
              </div>
              <div className="fg">
                <label className="fl">Reference Link <span style={{ color: 'var(--subtle)', fontWeight: 400 }}>(optional)</span></label>
                <input className="li" type="url" placeholder="https://…"
                  value={assignForm.link}
                  onChange={e => setAssignForm(f => ({ ...f, link: e.target.value }))} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="fg">
                  <label className="fl">Due Date</label>
                  <input className="li" type="date" required
                    value={assignForm.due_date}
                    onChange={e => setAssignForm(f => ({ ...f, due_date: e.target.value }))} />
                </div>
                <div className="fg">
                  <label className="fl">Max Score</label>
                  <input className="li" type="number" min="1" max="1000"
                    value={assignForm.max_score}
                    onChange={e => setAssignForm(f => ({ ...f, max_score: e.target.value }))} />
                </div>
              </div>
              <div className="fg" style={{ marginBottom: 0 }}>
                <label className="fl">Accepted File Types <span style={{ color: 'var(--subtle)', fontWeight: 400 }}>(comma-separated)</span></label>
                <input className="li" placeholder="PDF, DOCX, PPTX"
                  value={assignForm.accepted_file_types}
                  onChange={e => setAssignForm(f => ({ ...f, accepted_file_types: e.target.value }))} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
                <button type="button" className="btn btn-o" onClick={() => { setAssignModal(false); setSelAssign(null); }}>Cancel</button>
                <button type="submit" className="btn btn-p" disabled={savingAssign}>
                  {savingAssign ? 'Saving…' : selAssign ? 'Save Changes' : 'Create Assignment'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {toast && (
        <Toast
          message={typeof toast === 'string' ? toast : toast.msg}
          type={typeof toast === 'string' ? 'success' : toast.type}
          onDone={() => setToast(null)}
        />
      )}

      {showDelete && (
        <ConfirmModal
          title="Delete Course?"
          message="This course will be removed from the platform. Enrolled students will immediately lose access to all modules, quizzes and assignments."
          confirmLabel="Yes, Delete"
          onCancel={() => setShowDelete(false)}
          onConfirm={async () => {
            try { await deleteCourse(id); navigate('/admin/courses'); }
            catch (err) { setToast({ msg: err.message, type: 'error' }); setShowDelete(false); }
          }}
        />
      )}
    </>
  );
}
